import { clamp } from "../../core/math";
import { smoothSet } from "../envelope";

export interface DelayParams { timeSec: number; feedback: number; toneHz: number; returnLevel: number; }
export const DEFAULT_DELAY_PARAMS: DelayParams = { timeSec: 0.375, feedback: 0.35, toneHz: 4500, returnLevel: 0.7 };

/** Feedback delay with a low-pass filter in the loop. Feedback is capped < 1 for stability. */
export class DelayEffect {
  readonly input: GainNode;
  readonly output: GainNode;
  private delay: DelayNode;
  private fb: GainNode;
  private tone: BiquadFilterNode;
  private _params: DelayParams;

  constructor(private ctx: BaseAudioContext, params: Partial<DelayParams> = {}) {
    this._params = { ...DEFAULT_DELAY_PARAMS, ...params };
    this.input = ctx.createGain();
    this.delay = ctx.createDelay(2);
    this.fb = ctx.createGain();
    this.tone = ctx.createBiquadFilter();
    this.tone.type = "lowpass";
    this.output = ctx.createGain();
    this.input.connect(this.delay);
    this.delay.connect(this.tone).connect(this.fb).connect(this.delay);
    this.tone.connect(this.output);
    this.apply(true);
  }

  get params(): DelayParams { return this._params; }

  setParams(p: Partial<DelayParams>): void {
    this._params = { ...this._params, ...p };
    this.apply(false);
  }

  private apply(initial: boolean): void {
    const p = this._params;
    const set = (param: AudioParam, v: number) => (initial ? (param.value = v) : smoothSet(param, v, this.ctx, 0.03));
    set(this.delay.delayTime, clamp(p.timeSec, 0.01, 2));
    set(this.fb.gain, clamp(p.feedback, 0, 0.9));
    set(this.tone.frequency, clamp(p.toneHz, 200, 18000));
    set(this.output.gain, clamp(p.returnLevel, 0, 1));
  }

  dispose(): void {
    for (const n of [this.input, this.delay, this.fb, this.tone, this.output]) n.disconnect();
  }
}
